import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_API_URL || '/api';

export default function PromotionBanner({ placement }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || language;
  const [promo, setPromo] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const viewFired = useRef(false);

  useEffect(() => {
    axios.get(`${API}/promotions`, { params: { placement } })
      .then(r => { if (r.data.length > 0) setPromo(r.data[0]); })
      .catch(() => {});
  }, [placement]);

  // Fire view event once
  useEffect(() => {
    if (promo && !viewFired.current) {
      viewFired.current = true;
      axios.post(`${API}/promotions/${promo.id}/events`, { eventType: 'view' }).catch(() => {});
    }
  }, [promo]);

  if (!promo || dismissed) return null;

  const title = (language === 'fr' && promo.title_fr) ? promo.title_fr : promo.title_en;
  const body = (language === 'fr' && promo.body_fr) ? promo.body_fr : promo.body_en;
  const ctaText = (language === 'fr' && promo.cta_text_fr) ? promo.cta_text_fr : (promo.cta_text_en || null);

  const handleCTA = () => {
    axios.post(`${API}/promotions/${promo.id}/events`, { eventType: 'click' }).catch(() => {});
    if (!promo.cta_url) return;
    if (promo.cta_url.startsWith('/')) {
      // Prefix with lang if the URL doesn't already have a lang prefix
      const url = promo.cta_url.match(/^\/(fr|en)(\/|$)/) ? promo.cta_url : `/${currentLang}${promo.cta_url}`;
      navigate(url);
    } else {
      window.open(promo.cta_url, '_blank', 'noopener');
    }
  };

  const bgColor = promo.background_color || '#111827';
  const txtColor = promo.text_color || '#E8ECF0';

  // Sitewide banner — slim bar
  if (placement === 'sitewide_banner') {
    return (
      <div style={{
        background: bgColor, color: txtColor, padding: '0.6rem 2rem',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
        borderBottom: '1px solid rgba(148,163,184,0.15)', position: 'relative',
      }}>
        <span style={{ fontSize: '0.8rem', fontFamily: "'Barlow', sans-serif", fontWeight: 500 }}>{title}</span>
        {body && <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{body}</span>}
        {ctaText && (
          <button onClick={handleCTA} style={{
            background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.3rem 1rem',
            fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase',
            fontFamily: "'Barlow', sans-serif", fontWeight: 600, cursor: 'pointer',
          }}>{ctaText}</button>
        )}
        <button onClick={() => setDismissed(true)} style={{
          background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer',
          fontSize: '1.1rem', lineHeight: 1, padding: '0 0.3rem', position: 'absolute', right: '1rem',
        }}>&times;</button>
      </div>
    );
  }

  // Home hero — slim top banner above hero, below navbar
  if (placement === 'home_hero') {
    return (
      <div style={{
        background: `linear-gradient(90deg, ${bgColor} 0%, #0C1220 50%, ${bgColor} 100%)`,
        borderBottom: '1px solid rgba(212,43,43,0.25)',
        padding: '0.7rem 2rem', textAlign: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
        flexWrap: 'wrap', position: 'relative',
      }}>
        {promo.image_url && <img src={promo.image_url} alt="" style={{ maxHeight: '24px', objectFit: 'contain' }} />}
        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1rem', color: '#D42B2B', fontWeight: 600, letterSpacing: '0.03em' }}>{title}</span>
        {body && <span style={{ color: '#94A3B8', fontSize: '0.78rem', fontFamily: "'Barlow', sans-serif" }}>{body}</span>}
        {ctaText && (
          <button onClick={handleCTA} style={{
            background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.35rem 1.2rem',
            fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase',
            fontFamily: "'Barlow', sans-serif", fontWeight: 600, cursor: 'pointer',
          }}>{ctaText}</button>
        )}
        <button onClick={() => setDismissed(true)} style={{
          background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer',
          fontSize: '1.1rem', lineHeight: 1, padding: '0 0.3rem', position: 'absolute', right: '1rem',
        }}>&times;</button>
      </div>
    );
  }

  // Home inline — card section
  if (placement === 'home_inline') {
    return (
      <section style={{ padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 3rem)', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: bgColor, border: '1px solid rgba(148,163,184,0.15)',
          padding: '2.5rem', display: 'flex', alignItems: 'center', gap: '2rem', flexWrap: 'wrap',
        }}>
          {promo.image_url && <img src={promo.image_url} alt="" style={{ maxHeight: '120px', objectFit: 'contain', flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.6rem', color: txtColor, fontWeight: 500, marginBottom: '0.6rem' }}>{title}</h3>
            {body && <p style={{ color: '#94A3B8', fontSize: '0.85rem', lineHeight: 1.7, marginBottom: '1rem' }}>{body}</p>}
            {ctaText && (
              <button onClick={handleCTA} style={{
                background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.8rem 2rem',
                fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                fontFamily: "'Barlow', sans-serif", fontWeight: 600, cursor: 'pointer',
              }}>{ctaText}</button>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Services top — dismissible alert card
  if (placement === 'services_top') {
    return (
      <div style={{
        background: bgColor, border: '1px solid rgba(148,163,184,0.2)',
        padding: '1.5rem 2rem', marginBottom: '2rem', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: '1.5rem', flexWrap: 'wrap', position: 'relative',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flex: 1 }}>
          {promo.image_url && <img src={promo.image_url} alt="" style={{ maxHeight: '50px', objectFit: 'contain' }} />}
          <div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.2rem', color: txtColor, fontWeight: 500, marginBottom: '0.2rem' }}>{title}</div>
            {body && <div style={{ color: '#94A3B8', fontSize: '0.8rem' }}>{body}</div>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
          {ctaText && (
            <button onClick={handleCTA} style={{
              background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.6rem 1.5rem',
              fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase',
              fontFamily: "'Barlow', sans-serif", fontWeight: 600, cursor: 'pointer',
            }}>{ctaText}</button>
          )}
          <button onClick={() => setDismissed(true)} style={{
            background: 'transparent', border: 'none', color: '#94A3B8', cursor: 'pointer',
            fontSize: '1.2rem', lineHeight: 1, padding: '0 0.3rem',
          }}>&times;</button>
        </div>
      </div>
    );
  }

  // Booking sidebar — small card
  if (placement === 'booking_sidebar') {
    return (
      <div style={{
        background: bgColor, border: '1px solid rgba(148,163,184,0.15)',
        padding: '1.5rem', marginTop: '1.5rem',
      }}>
        {promo.image_url && <img src={promo.image_url} alt="" style={{ width: '100%', maxHeight: '100px', objectFit: 'contain', marginBottom: '1rem' }} />}
        <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem', color: txtColor, fontWeight: 500, marginBottom: '0.4rem' }}>{title}</div>
        {body && <div style={{ color: '#94A3B8', fontSize: '0.8rem', lineHeight: 1.6, marginBottom: '1rem' }}>{body}</div>}
        {ctaText && (
          <button onClick={handleCTA} style={{
            background: '#D42B2B', color: '#0C1220', border: 'none', padding: '0.6rem 1.2rem',
            fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase',
            fontFamily: "'Barlow', sans-serif", fontWeight: 600, cursor: 'pointer', width: '100%',
          }}>{ctaText}</button>
        )}
      </div>
    );
  }

  return null;
}
